import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import { getLuxandStats } from "../services/apis";
import { api } from "../services/apiClient";
import { SubHeader } from "../utils/componentes-utiles/SubHeader";

const toYmd = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const defaultFilters = () => {
	const today = new Date();
	const from = new Date(today);
	from.setDate(today.getDate() - 29);

	return {
		fechaDesde: toYmd(from),
		fechaHasta: toYmd(today),
	};
};

const formatNumber = (value) =>
	new Intl.NumberFormat("es-ES").format(Number(value || 0));

const formatPercent = (value) =>
	`${new Intl.NumberFormat("es-ES", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(Number(value || 0))}%`;

const BarList = ({ items = [], labelKey, valueKey = "total", color = "primary.main" }) => {
	const maxValue = Math.max(...items.map((item) => Number(item?.[valueKey] || 0)), 1);

	if (items.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				Sin datos para el periodo seleccionado.
			</Typography>
		);
	}

	return (
		<Box sx={{ display: "grid", gap: 1.5 }}>
			{items.map((item) => {
				const value = Number(item?.[valueKey] || 0);
				const width = `${Math.max(4, Math.round((value / maxValue) * 100))}%`;

				return (
					<Box key={`${item?.[labelKey]}-${value}`}>
						<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
							<Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
								{item?.[labelKey] || "-"}
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{formatNumber(value)}
							</Typography>
						</Box>
						<Box sx={{ height: 10, bgcolor: "grey.200", borderRadius: 1, overflow: "hidden" }}>
							<Box sx={{ width, height: "100%", bgcolor: color }} />
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};

export const Stats = () => {
	const initialFilters = useMemo(defaultFilters, []);
	const [fechaDesde, setFechaDesde] = useState(initialFilters.fechaDesde);
	const [fechaHasta, setFechaHasta] = useState(initialFilters.fechaHasta);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [stats, setStats] = useState(null);
	const [photoPreview, setPhotoPreview] = useState(null);
	const [photoLoading, setPhotoLoading] = useState(false);
	const [photoError, setPhotoError] = useState("");

	const cargarStats = useCallback(async () => {
		setLoading(true);
		setError("");

		try {
			const response = await getLuxandStats({
				fechaDesde,
				fechaHasta,
				limit: 12,
				logsLimit: 80,
			});

			if (!response?.success) {
				setError(response?.message || "No se pudieron cargar las estadisticas.");
				setStats(null);
				return;
			}

			setStats(response.data || null);
		} catch (err) {
			console.error("Error cargando estadisticas Luxand:", err);
			setError("No se pudieron cargar las estadisticas.");
			setStats(null);
		} finally {
			setLoading(false);
		}
	}, [fechaDesde, fechaHasta]);

	useEffect(() => {
		cargarStats();
	}, [cargarStats]);

	const byDay = Array.isArray(stats?.by_day) ? stats.by_day : [];
	const byStatus = Array.isArray(stats?.by_status) ? stats.by_status : [];
	const byEndpoint = Array.isArray(stats?.by_endpoint) ? stats.by_endpoint : [];
	const byRequestType = Array.isArray(stats?.by_request_type) ? stats.by_request_type : [];
	const byOperator = Array.isArray(stats?.by_operator) ? stats.by_operator : [];
	const byHour = Array.isArray(stats?.by_hour) ? stats.by_hour : [];
	const errorsByEndpoint = Array.isArray(stats?.errors_by_endpoint) ? stats.errors_by_endpoint : [];
	const recentLogs = Array.isArray(stats?.recent_logs) ? stats.recent_logs : [];
	const summary = stats?.summary || {};
	const errorSeverity = Number(summary.estimated_error_rate || summary.error_rate || 0) >= 10
		? "error"
		: Number(summary.estimated_error_rate || summary.error_rate || 0) >= 3
			? "warning"
			: "success";
	const formatScorePercent = (value) =>
		value !== null && value !== undefined ? formatPercent(Number(value) * 100) : "Sin dato";
	const getLogRowSx = (item) => {
		const isError = Boolean(item?.es_error_estimado);

		return {
			bgcolor: isError ? "#ffebee" : "#e8f5e9",
			"&:hover": {
				bgcolor: isError ? "#ffcdd2" : "#c8e6c9",
			},
			"& td": {
				borderColor: isError ? "#ffcdd2" : "#c8e6c9",
			},
		};
	};

	const abrirFotoLog = async (logItem) => {
		if (!logItem?.imagen_url || photoLoading) return;

		setPhotoLoading(true);
		setPhotoError("");
		setPhotoPreview({
			log: logItem,
			src: "",
		});

		try {
			const response = await api.get(logItem.imagen_url, { responseType: "blob" });
			const dataUrl = await new Promise((resolve) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result);
				reader.readAsDataURL(response.data);
			});

			setPhotoPreview({
				log: logItem,
				src: dataUrl,
			});
		} catch (err) {
			console.error("Error cargando foto del log Luxand:", err);
			setPhotoError("No se pudo cargar la foto de este log.");
		} finally {
			setPhotoLoading(false);
		}
	};

	const cerrarFotoLog = () => {
		setPhotoPreview(null);
		setPhotoError("");
		setPhotoLoading(false);
	};

	return (
		<Grid container justifyContent="center" sx={{ px: 2, pb: 4 }}>
			<Grid container size={{ xs: 12 }} sx={{ maxWidth: 1200 }}>
				<Grid size={{ xs: 12 }}>
					<SubHeader titulo="Estadisticas Luxand" />
				</Grid>

				<Grid size={{ xs: 12 }}>
					<Paper elevation={2} sx={{ p: 2, mb: 3 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid size={{ xs: 12, sm: 4, md: 3 }}>
								<TextField
									fullWidth
									size="small"
									type="date"
									label="Desde"
									value={fechaDesde}
									onChange={(event) => setFechaDesde(event.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 3 }}>
								<TextField
									fullWidth
									size="small"
									type="date"
									label="Hasta"
									value={fechaHasta}
									onChange={(event) => setFechaHasta(event.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4, md: 2 }}>
								<Button
									fullWidth
									variant="contained"
									startIcon={<RefreshIcon />}
									onClick={cargarStats}
									disabled={loading}
								>
									Actualizar
								</Button>
							</Grid>
						</Grid>
					</Paper>
				</Grid>

				{error && (
					<Grid size={{ xs: 12 }}>
						<Alert severity="error" sx={{ mb: 3 }}>
							{error}
						</Alert>
					</Grid>
				)}

				{loading ? (
					<Grid size={{ xs: 12 }}>
						<Box sx={{ minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
							<CircularProgress />
						</Box>
					</Grid>
				) : (
					<>
						<Grid container spacing={2} size={{ xs: 12 }} sx={{ mb: 3 }}>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Llamadas</Typography>
										<Typography variant="h4">{formatNumber(summary.total)}</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Errores</Typography>
										<Typography variant="h4">
											{formatNumber(summary.estimated_errors ?? summary.errors)}
										</Typography>
										<Chip
											size="small"
											color={errorSeverity}
											label={`Estimado ${formatPercent(summary.estimated_error_rate ?? summary.error_rate)}`}
											sx={{ mt: 1 }}
										/>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Media respuesta</Typography>
										<Typography variant="h4">
											{summary.avg_duration_ms !== null && summary.avg_duration_ms !== undefined
												? `${formatNumber(summary.avg_duration_ms)} ms`
												: "Sin dato"}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Min {summary.min_duration_ms ?? "-"} ms · Max {summary.max_duration_ms ?? "-"} ms
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Hora punta</Typography>
										<Typography variant="h4">
											{summary.peak_hour?.hora !== undefined
												? `${String(summary.peak_hour.hora).padStart(2, "0")}:00`
												: "-"}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.peak_hour?.total)} llamadas
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						<Grid container spacing={2} size={{ xs: 12 }} sx={{ mb: 3 }}>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Error real API</Typography>
										<Typography variant="h5">{formatPercent(summary.error_rate)}</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.errors)} por status/respuesta
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Error por umbral</Typography>
										<Typography variant="h5">{formatPercent(summary.threshold_error_rate)}</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.threshold_errors)} bajo umbral · {formatNumber(summary.response_json_errors)} por respuesta
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Score medio</Typography>
										<Typography variant="h5">
											{formatScorePercent(summary.avg_score)}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Min {summary.min_score ?? "-"} · Max {summary.max_score ?? "-"}
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Umbral usado</Typography>
										<Typography variant="body2">
											Desde cada llamada
										</Typography>
										<Typography variant="caption" color="text.secondary">
											request_payload_json.threshold o endpoint ?threshold=
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						<Grid container spacing={2} size={{ xs: 12 }} sx={{ mb: 3 }}>
							<Grid size={{ xs: 12, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Moda score</Typography>
										<Typography variant="h5">
											{formatScorePercent(summary.mode_score?.value)}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.mode_score?.count)} repeticion(es)
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Moda duracion</Typography>
										<Typography variant="h5">
											{summary.mode_duration_ms?.value !== undefined
												? `${formatNumber(summary.mode_duration_ms.value)} ms`
												: "Sin dato"}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.mode_duration_ms?.count)} repeticion(es)
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Operacion moda</Typography>
										<Typography variant="body1" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
											{summary.mode_request_type?.tipo || "Sin dato"}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.mode_request_type?.total)} llamadas
										</Typography>
									</CardContent>
								</Card>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }}>
								<Card>
									<CardContent>
										<Typography variant="body2" color="text.secondary">Endpoint moda</Typography>
										<Typography variant="body1" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
											{summary.mode_endpoint?.endpoint || "Sin dato"}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{formatNumber(summary.mode_endpoint?.total)} llamadas
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						<Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
							<Alert severity={errorSeverity}>
								{summary.top_error_endpoint?.endpoint
									? `Endpoint con mas errores estimados: ${summary.top_error_endpoint.endpoint} (${formatNumber(summary.top_error_endpoint.errores)} errores).`
									: `Tasa de exito API: ${formatPercent(summary.success_rate)}. La estimacion usa status, respuesta, liveness y puntuaciones frente a umbrales Luxand.`}
							</Alert>
						</Grid>

						<Grid container spacing={3} size={{ xs: 12 }}>
							<Grid size={{ xs: 12, md: 7 }}>
								<Paper elevation={2} sx={{ p: 2, height: "100%" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Llamadas por dia</Typography>
									<BarList items={byDay} labelKey="dia" />
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, md: 5 }}>
								<Paper elevation={2} sx={{ p: 2, height: "100%" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Errores estimados por dia</Typography>
									<BarList items={byDay} labelKey="dia" valueKey="errores_estimados" color="error.main" />
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Paper elevation={2} sx={{ p: 2, height: "100%" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Datos pedidos</Typography>
									<BarList items={byRequestType} labelKey="tipo" />
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Paper elevation={2} sx={{ p: 2, height: "100%" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Horas con mas actividad</Typography>
									<BarList items={byHour} labelKey="hora" />
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Paper elevation={2} sx={{ p: 2, height: "100%" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Estados</Typography>
									<BarList items={byStatus} labelKey="estado" />
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Paper elevation={2} sx={{ p: 2, height: "100%" }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Endpoints con errores estimados</Typography>
									<BarList items={errorsByEndpoint} labelKey="endpoint" valueKey="errores" color="error.main" />
								</Paper>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<Paper elevation={2} sx={{ p: 2 }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Evaluacion por endpoint</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Endpoint</TableCell>
												<TableCell align="right">Llamadas</TableCell>
												<TableCell align="right">Error API</TableCell>
												<TableCell align="right">Error estimado</TableCell>
												<TableCell align="right">Score medio</TableCell>
												<TableCell align="right">Umbral medio</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{byEndpoint.length === 0 ? (
												<TableRow>
													<TableCell colSpan={6}>Sin datos para el periodo seleccionado.</TableCell>
												</TableRow>
											) : (
												byEndpoint.map((item) => (
													<TableRow key={item.endpoint} hover>
														<TableCell sx={{ overflowWrap: "anywhere" }}>{item.endpoint}</TableCell>
														<TableCell align="right">{formatNumber(item.total)}</TableCell>
														<TableCell align="right">{formatNumber(item.errores)}</TableCell>
														<TableCell align="right">
															<Chip
																size="small"
																color={Number(item.estimated_error_rate || 0) > 0 ? "warning" : "success"}
																label={formatPercent(item.estimated_error_rate)}
															/>
														</TableCell>
														<TableCell align="right">{item.score_medio ?? "-"}</TableCell>
														<TableCell align="right">{item.umbral_medio ?? "-"}</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</Paper>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<Paper elevation={2} sx={{ p: 2 }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Usuarios que realizan peticiones</Typography>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Usuario</TableCell>
													<TableCell align="right">ID</TableCell>
													<TableCell align="right">Peticiones</TableCell>
													<TableCell align="right">Error API</TableCell>
													<TableCell align="right">Error estimado</TableCell>
													<TableCell align="right">Umbral</TableCell>
													<TableCell align="right">Respuesta</TableCell>
													<TableCell align="right">Media</TableCell>
													<TableCell align="right">Score medio</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{byOperator.length === 0 ? (
													<TableRow>
														<TableCell colSpan={9}>Sin usuarios para el periodo seleccionado.</TableCell>
													</TableRow>
												) : (
													byOperator.map((item, index) => (
														<TableRow key={`${item.id_usuario_operador ?? "sin-usuario"}-${index}`} hover>
															<TableCell sx={{ overflowWrap: "anywhere" }}>
																{item.usuario || "Sin usuario"}
															</TableCell>
															<TableCell align="right">{item.id_usuario_operador ?? "-"}</TableCell>
															<TableCell align="right">{formatNumber(item.total)}</TableCell>
															<TableCell align="right">
																{formatNumber(item.errores)}
																<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
																	{formatPercent(item.error_rate)}
																</Typography>
															</TableCell>
															<TableCell align="right">
																<Chip
																	size="small"
																	color={Number(item.estimated_error_rate || 0) > 0 ? "warning" : "success"}
																	label={formatPercent(item.estimated_error_rate)}
																/>
																<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
																	{formatNumber(item.errores_estimados)}
																</Typography>
															</TableCell>
															<TableCell align="right">{formatNumber(item.errores_umbral)}</TableCell>
															<TableCell align="right">{formatNumber(item.errores_respuesta)}</TableCell>
															<TableCell align="right">
																{item.avg_duration_ms !== null && item.avg_duration_ms !== undefined
																	? `${formatNumber(item.avg_duration_ms)} ms`
																	: "-"}
															</TableCell>
															<TableCell align="right">
																{item.score_medio !== null && item.score_medio !== undefined
																	? formatScorePercent(item.score_medio)
																	: "-"}
															</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<Paper elevation={2} sx={{ p: 2 }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Registro de logs</Typography>
									<TableContainer sx={{ maxHeight: 520 }}>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell>Fecha</TableCell>
													<TableCell>Usuario</TableCell>
													<TableCell>Operacion</TableCell>
													<TableCell>Endpoint</TableCell>
													<TableCell align="right">Estado</TableCell>
													<TableCell align="right">Score</TableCell>
													<TableCell align="right">Umbral</TableCell>
													<TableCell align="right">Duracion</TableCell>
													<TableCell align="right">Parte</TableCell>
													<TableCell align="right">Foto</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{recentLogs.length === 0 ? (
													<TableRow>
														<TableCell colSpan={10}>Sin logs para el periodo seleccionado.</TableCell>
													</TableRow>
												) : (
													recentLogs.map((item, index) => (
														<TableRow key={`${item.id_log || index}-${item.fecha || ""}`} hover sx={getLogRowSx(item)}>
															<TableCell sx={{ whiteSpace: "nowrap" }}>
																{item.fecha || "-"}
															</TableCell>
															<TableCell sx={{ overflowWrap: "anywhere", minWidth: 160 }}>
																{item.usuario_operador || (
																	item.id_usuario_operador ? `Usuario #${item.id_usuario_operador}` : "Sin usuario"
																)}
															</TableCell>
															<TableCell sx={{ overflowWrap: "anywhere" }}>
																{item.tipo || item.origen || "-"}
															</TableCell>
															<TableCell sx={{ overflowWrap: "anywhere", minWidth: 220 }}>
																{item.endpoint || "-"}
																{item.error_message && (
																	<Typography variant="caption" color="error" sx={{ display: "block" }}>
																		{item.error_message}
																	</Typography>
																)}
															</TableCell>
															<TableCell align="right">{item.status_code ?? "-"}</TableCell>
															<TableCell align="right">
																{item.score !== null && item.score !== undefined
																	? formatScorePercent(item.score)
																	: "-"}
															</TableCell>
															<TableCell align="right">
																{item.threshold_used !== null && item.threshold_used !== undefined
																	? formatScorePercent(item.threshold_used)
																	: "-"}
															</TableCell>
															<TableCell align="right">
																{item.duration_ms !== null && item.duration_ms !== undefined
																	? `${formatNumber(item.duration_ms)} ms`
																	: "-"}
															</TableCell>
															<TableCell align="right">{item.id_cuadrilla || "-"}</TableCell>
															<TableCell align="right">
																{item.imagen_url ? (
																	<Button
																		size="small"
																		variant="outlined"
																		startIcon={<PhotoCameraIcon />}
																		onClick={() => abrirFotoLog(item)}
																		disabled={photoLoading}
																		sx={{ whiteSpace: "nowrap" }}
																	>
																		Ver foto
																	</Button>
																) : (
																	<Chip size="small" label="Sin foto" />
																)}
															</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<Paper elevation={2} sx={{ p: 2 }}>
									<Typography variant="h6" sx={{ mb: 2 }}>Evaluacion por dato pedido</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Dato pedido</TableCell>
												<TableCell align="right">Llamadas</TableCell>
												<TableCell align="right">Error API</TableCell>
												<TableCell align="right">Error umbral</TableCell>
												<TableCell align="right">Error estimado</TableCell>
												<TableCell align="right">Score medio</TableCell>
												<TableCell align="right">Umbral medio</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{byRequestType.length === 0 ? (
												<TableRow>
													<TableCell colSpan={7}>Sin datos para el periodo seleccionado.</TableCell>
												</TableRow>
											) : (
												byRequestType.map((item) => (
													<TableRow key={item.tipo} hover>
														<TableCell sx={{ overflowWrap: "anywhere" }}>{item.tipo}</TableCell>
														<TableCell align="right">{formatNumber(item.total)}</TableCell>
														<TableCell align="right">{formatNumber(item.errores)}</TableCell>
														<TableCell align="right">{formatNumber(item.errores_umbral)}</TableCell>
														<TableCell align="right">
															<Chip
																size="small"
																color={Number(item.estimated_error_rate || 0) > 0 ? "warning" : "success"}
																label={formatPercent(item.estimated_error_rate)}
															/>
														</TableCell>
														<TableCell align="right">{item.score_medio ?? "-"}</TableCell>
														<TableCell align="right">{item.umbral_medio ?? "-"}</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</Paper>
							</Grid>
						</Grid>
					</>
				)}
			</Grid>
			<Dialog open={Boolean(photoPreview)} onClose={cerrarFotoLog} maxWidth="md" fullWidth>
				<DialogTitle>Foto del log</DialogTitle>
				<DialogContent sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
					{photoLoading ? (
						<CircularProgress />
					) : photoError ? (
						<Alert severity="error" sx={{ width: "100%" }}>
							{photoError}
						</Alert>
					) : photoPreview?.src ? (
						<Box
							component="img"
							src={photoPreview.src}
							alt="Foto del log"
							sx={{
								maxWidth: "100%",
								maxHeight: "70vh",
								objectFit: "contain",
								borderRadius: 1,
							}}
						/>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button onClick={cerrarFotoLog}>Cerrar</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
